// src/rms/components/CampaignForm.js
// ============================================================================
// Campaign create/edit — shared by the staff console and the advertiser portal
// ============================================================================
// One form, two audiences. `mode` decides what is editable:
//
//   "staff"       everything, including status, budget and approval
//   "advertiser"  targeting, flight, creatives — but NOT status, and never
//                 creative approval
//
// The restrictions here are a convenience, not the security boundary. RLS in
// 20260816150000_rms_advertiser_portal.sql enforces the same rules server-side,
// because a disabled input is one devtools edit away from being enabled.
// ============================================================================

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { VERTICAL_PRESETS } from "../verticals/presets";
import { IAB_SIZES } from "../ads/adInventory";
import i18n from "../i18n";
import { Modal, Field, TextInput, Select, Banner, StatusPill } from "./ui";

const PLACEMENTS = [
  { value: "sidebar", label: "Right rail (300×250)" },
  { value: "banner_top", label: "Header leaderboard (728×90)" },
];

const CREATIVE_TYPES = [
  { value: "native", label: "Native — we style it to match the interface", labelKey: "opt.creativeType.native" },
  { value: "image", label: "Image banner — your own artwork", labelKey: "opt.creativeType.image" },
  { value: "html", label: "HTML tag — third-party or affiliate snippet", labelKey: "opt.creativeType.html" },
];

function CampaignForm({ draft: initial, mode = "staff", advertisers = [], onCancel, onSave }) {
  // Rendered by /advertiser and /ad-sales, both outside RmsProvider, so it
  // translates through i18next directly rather than through useRms.
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState(null);
  const isStaff = mode === "staff";

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const toggle = (key, value) =>
    set(key, draft[key].includes(value) ? draft[key].filter((v) => v !== value) : [...draft[key], value]);

  const setCreative = (index, patch) =>
    set("creatives", draft.creatives.map((c, i) => (i === index ? { ...c, ...patch } : c)));

  const addCreative = () =>
    set("creatives", [
      ...draft.creatives,
      {
        name: `Creative ${draft.creatives.length + 1}`,
        creative_type: "native",
        iab_size: draft.placements.includes("banner_top") ? "leaderboard" : "medium_rectangle",
        headline: "",
        body: "",
        cta: i18n.t("campaignForm.learnMore", { defaultValue: "Learn more" }),
        accent: "#0f766e",
        imageUrl: "",
        html: "",
        click_url: "",
        review_status: "pending",
      },
    ]);

  const submit = () => {
    if (!draft.name.trim()) return setError("Give the campaign a name.");
    // An affiliate has no advertiser record and no insertion order: nobody
    // bought this, we placed it. Requiring an advertiser would mean inventing
    // one, and an invented advertiser turns up in avails and pacing as though
    // somebody were being billed.
    if (isStaff && !draft.is_affiliate && !draft.advertiser_id) {
      return setError("Choose an advertiser.");
    }
    if (draft.is_affiliate && !draft.name.trim()) {
      return setError("Name the affiliate program, so the disclosure can say who it is.");
    }
    if (draft.is_affiliate) {
      const unlinked = draft.creatives.find((c) => !String(c.click_url || "").trim());
      if (unlinked) return setError(`"${unlinked.name}" needs its tracking link.`);
    }
    if (!draft.ad_categories.length) return setError("Choose at least one vertical to target.");
    if (!draft.placements.length) return setError("Choose at least one placement.");
    if (!draft.starts_at || !draft.ends_at) return setError("Set the flight dates.");
    if (new Date(draft.ends_at) <= new Date(draft.starts_at)) {
      return setError("The flight must end after it starts.");
    }
    if (!draft.creatives.length) return setError("Add at least one creative.");

    const incomplete = draft.creatives.find((c) =>
      c.creative_type === "image" ? !c.imageUrl : c.creative_type === "html" ? !c.html : !c.headline
    );
    if (incomplete) return setError(`"${incomplete.name}" is missing its content.`);

    setError(null);
    onSave(draft);
  };

  return (
    <Modal
      title={draft.id ? t("campaignForm.edit", "Edit {name}", { name: draft.name }) : t("campaignForm.new", "New campaign")}
      onClose={onCancel}
      width={760}
      footer={
        <>
          <button type="button" className="rms-btn rms-btn--ghost" onClick={onCancel}>{t("action.cancel", "Cancel")}</button>
          <button type="button" className="rms-btn rms-btn--primary" onClick={submit}>
            {isStaff ? t("campaignForm.save", "Save campaign") : draft.id ? t("action.saveChanges", "Save changes") : t("campaignForm.submit", "Submit for review")}
          </button>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}

      {!isStaff ? (
        <Banner tone="info">
          {t("campaignForm.campaignsAndCreativeGo", "Campaigns and creative go to our team for review before they run. You'll see the status change here once they're approved — usually within one working day.")}
        </Banner>
      ) : null}

      {/* An affiliate is not a sold flight. It carries no budget, meets no rate
          floor, and fills behind sold demand rather than competing with it --
          so the switch changes what the rest of this form asks for, not just a
          label. Staff only: an advertiser in the portal is by definition
          buying, not affiliating. */}
      {isStaff ? (
        <label className="rms-checkline" style={{ marginBottom: 12 }}>
          <input
            type="checkbox"
            checked={!!draft.is_affiliate}
            onChange={(e) => set("is_affiliate", e.target.checked)}
          />
          {t("campaignForm.isAffiliate", "Affiliate program (no advertiser, no budget — paid on clicks and conversions)")}
        </label>
      ) : null}

      {draft.is_affiliate ? (
        <Banner tone="info">
          {t("campaignForm.affiliateHelp", "Paste the network's tracking link as each creative's click URL. It will be labelled \"Affiliate\" to staff and carry rel=\"sponsored nofollow\", which the networks require. It fills only when no sold campaign wants the slot.")}
        </Banner>
      ) : null}

      {/* -- basics -- */}
      <div className="rms-field-grid">
        <Field label={t("campaignForm.campaignName", "Campaign name")} required>
          <TextInput value={draft.name} onChange={(v) => set("name", v)} placeholder={t("campaignForm.q3FleetInsurancePush", "Q3 fleet insurance push")} />
        </Field>
        {isStaff ? (
          <Field label={t("campaignForm.advertiser", "Advertiser")} required>
            <Select
              value={draft.advertiser_id}
              onChange={(v) => set("advertiser_id", v)}
              placeholder="Choose…"
              options={advertisers.map((a) => ({ value: a.id, label: a.name }))}
            />
          </Field>
        ) : null}
      </div>

      {/* -- targeting -- */}
      <div className="rms-field">
        <label className="rms-label">
          {t("campaignForm.targetVerticals", "Target verticals")}<span className="rms-required">*</span>
        </label>
        <div className="rms-help" style={{ marginBottom: 6 }}>
          {t("campaignForm.yourAdOnlyShows", "Your ad only shows to operators who run this kind of rental business.")}
        </div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {VERTICAL_PRESETS.map((p) => (
            <button
              key={p.adCategory}
              type="button"
              className={`rms-btn rms-btn--sm ${draft.ad_categories.includes(p.adCategory) ? "rms-btn--primary" : "rms-btn--ghost"}`}
              onClick={() => toggle("ad_categories", p.adCategory)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rms-field">
        <label className="rms-label">
          {t("campaignForm.placements", "Placements")}<span className="rms-required">*</span>
        </label>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {PLACEMENTS.map((p) => (
            <button
              key={p.value}
              type="button"
              className={`rms-btn rms-btn--sm ${draft.placements.includes(p.value) ? "rms-btn--primary" : "rms-btn--ghost"}`}
              onClick={() => toggle("placements", p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* -- flight and money -- */}
      <div style={{ borderTop: "1px solid var(--rms-border)", paddingTop: 14, marginTop: 6 }}>
        <h3 className="rms-section-title">{t("campaignForm.flight", "Flight")}</h3>
        <div className="rms-field-grid">
          <Field label={t("campaignForm.starts", "Starts")} required>
            <input className="rms-input" type="date" value={draft.starts_at} onChange={(e) => set("starts_at", e.target.value)} />
          </Field>
          <Field label={t("campaignForm.ends", "Ends")} required>
            <input className="rms-input" type="date" value={draft.ends_at} onChange={(e) => set("ends_at", e.target.value)} />
          </Field>
          <Field label={t("campaignForm.budget", "Budget")} help={isStaff ? undefined : "Set with your account manager on the insertion order."}>
            <input
              className="rms-input"
              type="number"
              min="0"
              step="0.01"
              value={draft.budget}
              disabled={!isStaff}
              onChange={(e) => set("budget", e.target.value)}
            />
          </Field>
          <Field label={t("campaignForm.impressionGoal", "Impression goal")}>
            <input
              className="rms-input"
              type="number"
              min="0"
              value={draft.goal_impressions}
              disabled={!isStaff}
              onChange={(e) => set("goal_impressions", e.target.value)}
            />
          </Field>
        </div>

        {isStaff ? (
          <div className="rms-field-grid">
            <Field label="CPM" help={t("campaignForm.centsPerThousandViewable", "Cents per thousand viewable impressions.")}>
              <input className="rms-input" type="number" min="0" value={draft.cpm} onChange={(e) => set("cpm", e.target.value)} />
            </Field>
            <Field label="CPC">
              <input className="rms-input" type="number" min="0" value={draft.cpc} onChange={(e) => set("cpc", e.target.value)} />
            </Field>
            <Field label={t("campaignForm.pacing", "Pacing")} help={t("campaignForm.evenSpreadsTheBudget", "Even spreads the budget across the flight.")}>
              <Select
                value={draft.pacing}
                onChange={(v) => set("pacing", v)}
                placeholder={t("campaignForm.even", "Even")}
                options={[
                  { value: "even", label: "Even — spread across the flight", labelKey: "opt.pacing.even" },
                  { value: "asap", label: "ASAP — deliver as fast as possible", labelKey: "opt.pacing.asap" },
                ]}
              />
            </Field>
            <Field label={t("campaignForm.frequencyCap", "Frequency cap")} help="Max times one operator sees this per day. Blank = uncapped.">
              <input className="rms-input" type="number" min="1" value={draft.frequency_cap_per_day} onChange={(e) => set("frequency_cap_per_day", e.target.value)} />
            </Field>
          </div>
        ) : null}

        {isStaff ? (
          <Field label={t("campaignForm.status", "Status")} help={t("campaignForm.onlyLiveServesEverything", "Only 'live' serves. Everything else is held.")}>
            <Select
              value={draft.status}
              onChange={(v) => set("status", v)}
              placeholder={t("campaignForm.draft", "Draft")}
              options={[
                { value: "draft", label: "Draft", labelKey: "opt.campaignStatus.draft" },
                { value: "pending_approval", label: "Pending approval", labelKey: "opt.campaignStatus.pendingApproval" },
                { value: "approved", label: "Approved — not yet running", labelKey: "opt.campaignStatus.approved" },
                { value: "live", label: "Live — serving now", labelKey: "opt.campaignStatus.live" },
                { value: "paused", label: "Paused", labelKey: "opt.campaignStatus.paused" },
                { value: "completed", label: "Completed", labelKey: "opt.campaignStatus.completed" },
                { value: "rejected", label: "Rejected", labelKey: "opt.campaignStatus.rejected" },
              ]}
            />
          </Field>
        ) : (
          <Field label={t("campaignForm.status", "Status")}>
            <div><StatusPill status={draft.status === "live" ? "active" : "pending"} label={draft.status.replace(/_/g, " ")} /></div>
          </Field>
        )}
      </div>

      {/* -- creatives -- */}
      <div style={{ borderTop: "1px solid var(--rms-border)", paddingTop: 14, marginTop: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h3 className="rms-section-title" style={{ margin: 0 }}>{t("campaignForm.creatives", "Creatives")}</h3>
          <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={addCreative}>
            + Add creative
          </button>
        </div>

        {draft.creatives.length === 0 ? (
          <p className="rms-help">{t("campaignForm.addAtLeastOne", "Add at least one. Several rotate evenly within the campaign.")}</p>
        ) : null}

        {draft.creatives.map((c, i) => (
          <div key={i} className="rms-card rms-card-pad" style={{ marginBottom: 10, background: "var(--rms-subtle)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <strong style={{ fontSize: 13.5 }}>{c.name}</strong>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <StatusPill
                  status={c.review_status === "approved" ? "active" : c.review_status === "rejected" ? "cancelled" : "pending"}
                  label={c.review_status}
                />
                <button
                  type="button"
                  className="rms-btn rms-btn--ghost rms-btn--sm"
                  onClick={() => set("creatives", draft.creatives.filter((_, idx) => idx !== i))}
                >
                  {t("campaignForm.remove", "Remove")}
                </button>
              </div>
            </div>

            <div className="rms-field-grid">
              <Field label={t("campaignForm.name", "Name")}>
                <TextInput value={c.name} onChange={(v) => setCreative(i, { name: v })} />
              </Field>
              <Field label={t("campaignForm.format", "Format")}>
                <Select value={c.creative_type} onChange={(v) => setCreative(i, { creative_type: v })} options={CREATIVE_TYPES} placeholder={t("campaignForm.native", "Native")} />
              </Field>
              <Field label={t("campaignForm.size", "Size")}>
                <Select
                  value={c.iab_size}
                  onChange={(v) => setCreative(i, { iab_size: v })}
                  placeholder={t("campaignForm.mediumRectangle", "Medium rectangle")}
                  options={Object.entries(IAB_SIZES).map(([k, s]) => ({
                    value: k.toLowerCase(),
                    label: `${s.name} — ${s.width}×${s.height}`,
                  }))}
                />
              </Field>
              <Field label={t("campaignForm.clickThroughUrl", "Click-through URL")} required>
                <TextInput value={c.click_url} onChange={(v) => setCreative(i, { click_url: v })} placeholder="https://" />
              </Field>
            </div>

            {c.creative_type === "native" ? (
              <div className="rms-field-grid">
                <Field label={t("campaignForm.headline", "Headline")} required>
                  <TextInput value={c.headline} onChange={(v) => setCreative(i, { headline: v })} />
                </Field>
                <Field label={t("campaignForm.callToAction", "Call to action")}>
                  <TextInput value={c.cta} onChange={(v) => setCreative(i, { cta: v })} />
                </Field>
                <Field label={t("campaignForm.body", "Body")}>
                  <TextInput value={c.body} onChange={(v) => setCreative(i, { body: v })} />
                </Field>
                <Field label={t("campaignForm.accentColour", "Accent colour")}>
                  <input className="rms-input" type="color" value={c.accent} onChange={(e) => setCreative(i, { accent: e.target.value })} />
                </Field>
              </div>
            ) : c.creative_type === "image" ? (
              <Field label={t("campaignForm.imageUrl", "Image URL")} required help={t("campaignForm.mustMatchTheSelected", "Must match the selected size exactly, or it will be rejected at review.")}>
                <TextInput value={c.imageUrl} onChange={(v) => setCreative(i, { imageUrl: v })} placeholder="https://cdn.example.com/banner-300x250.png" />
              </Field>
            ) : (
              <Field
                label={t("campaignForm.htmlTag", "HTML tag")}
                required
                help={t("campaignForm.runsInASandboxed", "Runs in a sandboxed iframe with no access to the page. Scripts are allowed; same-origin access is not.")}
              >
                <textarea className="rms-textarea" value={c.html} onChange={(e) => setCreative(i, { html: e.target.value })} />
              </Field>
            )}

            {isStaff && c.review_status !== "approved" ? (
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button type="button" className="rms-btn rms-btn--primary rms-btn--sm" onClick={() => setCreative(i, { review_status: "approved" })}>
                  {t("campaignForm.approve", "Approve")}
                </button>
                <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => setCreative(i, { review_status: "rejected" })}>
                  {t("campaignForm.reject", "Reject")}
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </Modal>
  );
}

/** Blank draft. `advertiserId` pre-binds it in the advertiser portal. */
export function blankCampaign(advertiserId = "") {
  const today = new Date();
  const in30 = new Date(today.getTime() + 30 * 864e5);
  const iso = (d) => d.toISOString().slice(0, 10);

  return {
    id: null,
    advertiser_id: advertiserId,
    is_affiliate: false,
    name: "",
    ad_categories: [],
    placements: ["sidebar"],
    starts_at: iso(today),
    ends_at: iso(in30),
    budget: "",
    goal_impressions: "",
    cpm: "",
    cpc: "",
    pacing: "even",
    frequency_cap_per_day: "",
    status: "draft",
    creatives: [],
  };
}

/** DB rows → form draft. */
export function toCampaignDraft(campaign, creatives = []) {
  return {
    id: campaign.id,
    advertiser_id: campaign.advertiser_id || "",
    is_affiliate: !!campaign.is_affiliate,
    name: campaign.name,
    ad_categories: campaign.ad_categories || [],
    placements: campaign.placements || [],
    starts_at: (campaign.starts_at || "").slice(0, 10),
    ends_at: (campaign.ends_at || "").slice(0, 10),
    budget: campaign.budget_cents != null ? (campaign.budget_cents / 100).toString() : "",
    goal_impressions: campaign.goal_impressions ?? "",
    cpm: campaign.cpm_cents ?? "",
    cpc: campaign.cpc_cents ?? "",
    pacing: campaign.pacing || "even",
    frequency_cap_per_day: campaign.frequency_cap_per_day ?? "",
    status: campaign.status || "draft",
    creatives: creatives.map((c) => ({
      id: c.id,
      name: c.name,
      creative_type: c.creative_type,
      iab_size: c.iab_size,
      click_url: c.click_url || "",
      review_status: c.review_status,
      ...(c.payload || {}),
    })),
  };
}

export default CampaignForm;
